<template>
  <component :is="componentName()" class="p-article-link" v-bind="linkAttributes()">
    <div v-bind="labelAttributes()" v-text="label" />
    <p-icon v-if="appendIcon" :icon="appendIcon" size="xs" />
  </component>
</template>

<script lang="ts" setup>
import { computed, resolveComponent } from 'vue'

import { sanitizeUrl } from '@/types/content'

const props = defineProps({
  label: {
    type: String,
    default: null,
  },
  target: {
    type: String,
    default: '_self',
    validator: (val: string) => ['_self', '_blank'].includes(val),
  },
  size: {
    type: String,
    default: 'sm',
    validator: (val: string) => ['sm', 'md', 'lg'].includes(val),
  },
  appendIcon: {
    type: String,
    default: null,
  },
  passive: {
    type: Boolean,
    default: false,
  },
  href: {
    type: String,
    default: null,
  },
  to: {
    type: [String, Object],
    default: null,
  },
})

const componentName = () => {
  return props.passive ? 'span' : resolveComponent('NuxtLink')
}

// Sanitize URLs to prevent XSS via javascript: protocol
const safeHref = computed(() => {
  if (typeof props.href === 'string') {
    return sanitizeUrl(props.href)
  }
  return props.href
})

const safeTo = computed(() => {
  if (typeof props.to === 'string') {
    return sanitizeUrl(props.to)
  }
  return props.to
})

const linkAttributes = (): { target?: string } => {
  return {
    [props.href ? 'href' : 'to']: safeHref.value || safeTo.value,
    ...(!props.passive && {
      target: props.target,
    }),
  }
}

const labelAttributes = (): { class: Record<string, boolean> } => {
  return {
    class: {
      'p-article-link__label': true,
      [`p-article-link__label--${props.size}`]: !!props.size,
    },
  }
}
</script>
